import type {
  ClarificationReason,
  SolConflictBasis,
  SolMemoryKind,
  SolMemoryOrigin,
  SolMemoryRefusalReason,
  SolReadDomain,
  SolReasoningFailureKind,
  SolScopeFacts,
  UnsupportedReason,
} from '../../data/sol/sol-contract';

/**
 * EVERY WORD SOL'S SCREEN SAYS THAT SOL DID NOT WRITE. M9 / S09-04.
 *
 * **DETERMINISTIC, AND NEVER GENERATED.** No sentence in this file comes from a
 * model, and none may: a warning written by the thing being warned about is not
 * a warning. There is exactly one model-authored string on the whole Sol
 * screen — a validated claim's own text — and everything framing it is here.
 *
 * **NO INTERNAL VOCABULARY SURVIVES THIS FILE.** `source_not_sent`,
 * `malformed_root`, `conflict_basis_invalid` and every other
 * `SolRejectionReason` are engineering words. They are never rendered, never
 * appended "for support", and never used to pick a message — the mapping below
 * is keyed on the FAILURE KIND alone, so a validator reason has no path to a
 * screen at all.
 *
 * **FACTS, NOT LABELS.** "The records disagree" is a fact about his Captures.
 * "answerState: conflicting" is a fact about Crewlink's data model, and he did
 * not ask about Crewlink's data model.
 */

/**
 * The one persistent warning, shown ONCE on the surface rather than stamped on
 * every answer. Repetition is how a warning becomes furniture.
 */
export const SOL_STANDING_WARNING =
  'Sol can make mistakes. Verify important information against Crewlink sources.';

export const SOL_EMPTY_PROMPT =
  "Ask about anything you've captured. Sol answers from your real Captures, not from memory.";

/**
 * **THE ONE SAFE PROJECT FALLBACK.**
 *
 * `SolProjectRef.name` is genuinely nullable — the Project row may not have
 * been readable at hydration time. **`project.name ?? project.id` IS A BANNED
 * PATTERN**: it puts an internal UUID on a construction site's screen, and one
 * day into a spoken sentence. A Project whose name Crewlink could not read is
 * honestly "another Project", and that is all it may ever be called.
 */
export const SAFE_PROJECT_FALLBACK = 'another Project';

export function projectLabel(name: string | null): string {
  const trimmed = name?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : SAFE_PROJECT_FALLBACK;
}

// ===========================================================================
// Scope disclosure (D-04-1)
// ===========================================================================

/**
 * What Crewlink searched, in one short truthful line.
 *
 * **DERIVED ENTIRELY FROM SERVER-OWNED SCOPE FACTS AND THE PROJECT ALREADY ON
 * SCREEN.** No Project is looked up to decorate this sentence, no Project name
 * is enumerated, and no raw scope object is ever shown. Broadening is disclosed
 * because it changed what was searched; the exact list of other Projects is not,
 * because naming them would cost a database read that adds nothing he can act
 * on.
 */
export function describeScope(scope: SolScopeFacts, currentProjectId: string | null): string {
  if (scope.broadenedFrom !== null) return 'Also searched other Projects.';
  if (scope.requestedScope.kind === 'global') return 'Searched all your Projects.';
  if (currentProjectId !== null && scope.requestedScope.projectId === currentProjectId) {
    return 'Searched this Project.';
  }
  return 'Searched one Project.';
}

/** How much of what matched Sol actually read. Both numbers are the server's. */
export function describeEvidenceBound(sent: number, total: number): string {
  return `Sol answered from the ${sent} closest of ${total} matching Captures.`;
}

/**
 * The Project list was cut short. **Crewlink's own sentence, and it has to
 * exist**: the model is forbidden from describing the search, so without this
 * a list of the 25 most recent Projects reads as "these are all my Projects".
 * The true total is deliberately NOT stated — the lookup asked for one page and
 * one row beyond it, which proves there are more without counting them, and
 * inventing a total here would be the guess this whole layer exists to prevent.
 */
export function describeProjectListBound(sent: number): string {
  return `Sol listed the ${sent} most recently created Projects. There are more.`;
}

/**
 * An EXACT count. **Only ever used where the database answered exactly.**
 *
 * See `describeCloseMatchCount` for why this one must not be reused for close
 * matches — sharing it is the defect Jerry found on the phone.
 */
export function describeCount(total: number): string {
  return total === 1 ? '1 matching Capture.' : `${total} matching Captures.`;
}

/** A count of PROJECTS — the database's number, never a loaded list (M11). */
export function describeProjectCount(total: number): string {
  return total === 1 ? '1 Project.' : `${total} Projects.`;
}

/** A count of PHOTOS — the database's number (M11 / I-071). */
export function describePhotoCount(total: number): string {
  return total === 1 ? '1 photo.' : `${total} photos.`;
}

/**
 * A count of CLOSE matches, which is a count of GUESSES.
 *
 * **THIS EXISTS BECAUSE SHARING `describeCount` PRODUCED A FALSE SENTENCE.**
 * Asked what he had recorded about the roof, Brandon was shown three near-miss
 * Captures under the words *"3 matching Captures."* — read that on a site and it
 * plainly means Crewlink found three roof records. He opened one, found no roof
 * in it, and was right to be confused. **The retrieval was correct; the sentence
 * was not.**
 *
 * The number is still worth showing: it is the true size of the close-match set
 * from the database, and it can be larger than the number of candidates listed.
 * What it needed was a noun that says "guess" rather than "match".
 */
export function describeCloseMatchCount(total: number): string {
  return total === 1 ? '1 close match.' : `${total} close matches.`;
}

// ===========================================================================
// Claim-level qualifiers
// ===========================================================================

/**
 * Said QUIETLY and at the CLAIM level (D-04-5 / the inference rule). A
 * supported claim sitting beside an inferred one is still supported, and
 * labelling the whole answer would demote it for no reason.
 */
export const INFERENCE_NOTE =
  'Sol worked this out from the records rather than reading it directly.';

export function conflictNote(basis: SolConflictBasis): string {
  return basis === 'within_source'
    ? 'One record contradicts itself here.'
    : 'Two records disagree here.';
}

/**
 * **CREWLINK DOES NOT PICK A WINNER** (PT: conflicting evidence is reported,
 * never resolved). This line frames the claims; it never replaces them.
 */
export const CONFLICT_ANSWER_NOTE = 'The records disagree, so both readings are shown.';

// ===========================================================================
// Clarification
// ===========================================================================

function quoted(phrase: string | null): string | null {
  const trimmed = phrase?.trim();
  return trimmed && trimmed.length > 0 ? `“${trimmed}”` : null;
}

/**
 * What Sol needs before it will search.
 *
 * **A CLARIFICATION IS NOT A FAILURE**, so none of these apologise and none of
 * them offer Retry — the same request would produce the same question.
 */
export function clarificationText(reason: ClarificationReason, phrase: string | null): string {
  const said = quoted(phrase);
  switch (reason) {
    case 'ambiguous_project_name':
      return said === null
        ? 'More than one of your Projects has that name.'
        : `More than one of your Projects is called ${said}.`;
    case 'unresolved_project_name':
      return said === null
        ? 'No Project of yours has that name.'
        : `No Project of yours is called ${said}.`;
    case 'project_context_required':
      // **A QUESTION, AND THE SAME ONE WHATEVER HE SAID.** Reached when a read
      // asked for a Project scope and none resolved — from Global Sol, or with
      // no Project under discussion yet. It is phrase-independent on purpose,
      // and Crewlink now lists his own Projects underneath it as real choices,
      // so the sentence has to read as the question those choices answer.
      return 'Which Project do you mean? Name it in your question, or open the Project and ask again.';
    case 'scope_undetermined':
      return 'Sol needs to know where to look. Name a Project, or ask from inside one.';
    case 'contradictory_scope':
      return 'That asks Sol to stay in one Project and to look everywhere, in the same question.';
    case 'ambiguous_physical_identifier':
      return said === null
        ? 'That place exists in more than one of your Projects. Name the Project, or ask from inside it.'
        : `${said} exists in more than one of your Projects. Name the Project, or ask from inside it.`;
    case 'near_project_name':
      // **"DID YOU MEAN" IS THE WHOLE PRODUCT DECISION IN ONE SENTENCE.** He
      // named a Project that is almost certainly one of his; Crewlink will not
      // say it does not exist, and will not pick it for him either. The
      // candidate cards carry the real names, so the sentence stays short and
      // never quotes a name back at him.
      return 'Did you mean one of these Projects?';
    case 'ambiguous_status_language':
      return said === null
        ? 'That could mean Repaired or Closed, and those are different. Say which one.'
        : `${said} could mean Repaired or Closed, and those are different. Say which one.`;
    default: {
      // A reason this build has never heard of. **Say the true general thing**
      // rather than printing the code.
      const exhaustive: never = reason;
      void exhaustive;
      return 'Sol needs one more detail before it can search.';
    }
  }
}

// ===========================================================================
// Unsupported
// ===========================================================================

/**
 * Language outside Sol's grammar.
 *
 * **THESE ARE HONEST "NOT YET"s, NOT FAULTS.** Every one of them names what he
 * can do instead, because "Sol can't do that" with no next step is a dead end.
 */
export function unsupportedText(reason: UnsupportedReason, phrase: string): string {
  const said = quoted(phrase);
  const because = said === null ? '' : ` (${said})`;
  switch (reason) {
    case 'negation':
      return `Sol can't answer questions about what is missing or did not happen${because}. Ask for what the records do say.`;
    case 'unsupported_relative_date':
      return `Sol can't work that date out yet${because}. Try today, yesterday, or say what you are looking for instead.`;
    case 'superlative_or_comparison':
      return `Sol can't rank or compare Captures yet${because}. Ask about one thing at a time.`;
    case 'conversational_reference':
      return `Sol answers each question on its own, so it can't follow a reference back to an earlier one${because}. Ask it in full.`;
    case 'multi_project_comparison':
      return `Sol can't compare two Projects in one question yet${because}. Ask about one Project at a time.`;
    default: {
      const exhaustive: never = reason;
      void exhaustive;
      return "Sol can't answer a question phrased that way yet.";
    }
  }
}

// ===========================================================================
// Failure
// ===========================================================================

export interface SolFailureWording {
  readonly text: string;
  /**
   * **RETRY IS OFFERED ONLY WHERE REPEATING THE REQUEST COULD HONESTLY GO
   * DIFFERENTLY.** A switched-off Sol will be switched off again a second
   * later, and a Retry button there teaches him to tap something that cannot
   * work.
   */
  readonly retryable: boolean;
}

/**
 * Internal failure kinds → Brandon's words.
 *
 * **NOTHING IN HERE NAMES A PROVIDER, A MODEL, AN ENVIRONMENT VARIABLE, AN HTTP
 * STATUS OR A SUPABASE INTERNAL**, and the `SolRejectionReason` that accompanied
 * the failure is deliberately not consulted at all.
 *
 * **`reasoning_disabled` IS A CONFIGURATION STATE, NOT A BREAKAGE** — calling it
 * "AI unavailable" would tell him something is wrong with his app when nothing
 * is. Crewlink's search and counting still work, and the message says so.
 */
export function failureWording(failure: SolReasoningFailureKind): SolFailureWording {
  switch (failure) {
    case 'reasoning_disabled':
      return {
        text: "Sol's answering is not switched on for this Crewlink yet. Searching and counting still work.",
        retryable: false,
      };
    case 'reasoning_unconfigured':
      return {
        text: "Sol's answering is not set up yet. Searching and counting still work.",
        retryable: false,
      };
    case 'provider_unavailable':
      return { text: "Sol couldn't be reached just now.", retryable: true };
    case 'sol_busy':
      // TRUE, and it names the actual next action: wait a moment.
      return {
        text: 'Sol is already working on another request. Try again in a moment.',
        retryable: true,
      };
    case 'sol_rate_limited':
      return {
        text: 'Sol has reached its temporary usage limit. Try again later.',
        retryable: true,
      };
    case 'provider_incomplete':
      return { text: "Sol started an answer and didn't finish it.", retryable: true };
    case 'invalid_model_output':
      return {
        text: "Sol's answer didn't come back in a form Crewlink could check, so it was not shown.",
        retryable: true,
      };
    case 'citation_validation_failed':
      return {
        text: "Sol's answer pointed at records Crewlink never gave it, so it was not shown.",
        retryable: true,
      };
    case 'my_day_timezone_unset':
      // **IT NAMES THE FIX**, because this one is his to fix in a tap: the
      // Settings time zone decides what "today" means (PRODUCT_TRUTH §13.1).
      return {
        text: 'Crewlink needs your time zone in Settings before it can work out your day.',
        retryable: false,
      };
    case 'internal_error':
      return { text: "Sol couldn't complete that answer.", retryable: true };
    default: {
      // **UNMAPPED MEANS GENERIC, NEVER THE RAW CODE.** A server that grows a
      // new failure kind must not be able to print it onto his screen.
      const exhaustive: never = failure;
      void exhaustive;
      return { text: "Sol couldn't complete that answer.", retryable: false };
    }
  }
}

/** Truthful when reasoning failed but retrieval had already succeeded. */
export function describeFoundButUnanswered(total: number): string {
  return total === 1
    ? "Crewlink found 1 matching Capture, but Sol couldn't complete the answer."
    : `Crewlink found ${total} matching Captures, but Sol couldn't complete the answer.`;
}

// ===========================================================================
// Everything else the screen says
// ===========================================================================

/**
 * AN EMPTY READ, WORDED AS THE THING IT WAS ABOUT.
 *
 * **The domain is structured data the server derived from the capability it
 * executed** (`SolReadDomain`); nothing here classifies language. One
 * Capture-only sentence used to serve every empty read, so zero saved reports
 * read as "Crewlink found no Captures matching that." The switch is exhaustive
 * and closed by `never`: a sixth read domain is a compile error, not a Capture
 * sentence.
 *
 * The scope decides the noun for the scoped kinds — "this Project" when the
 * read was about the Project on screen, "that Project" for another one, and
 * no Project at all for a read across everything. A company read and a
 * Project read are not scoped to a Project, so they say nothing about one.
 */
export function describeNoEvidence(
  domain: SolReadDomain,
  scope: SolScopeFacts,
  currentProjectId: string | null,
): string {
  const where = scopeNoun(scope, currentProjectId);
  switch (domain) {
    case 'capture':
      return 'Crewlink found no Captures matching that.';
    case 'project':
      return 'Crewlink found no Projects matching that.';
    case 'report':
      return where === null
        ? 'There are no saved reports yet.'
        : `There are no saved reports for ${where}.`;
    case 'photo':
      return where === null
        ? 'Crewlink found no photos matching that.'
        : `Crewlink found no photos matching that in ${where}.`;
    case 'company':
      return 'No company details are stored yet.';
    case 'conversation':
      // **HONEST EMPTY RECALL** (M11 / D-041): nothing earlier matched. Not a
      // Capture sentence, not a failure — there was simply no such chat.
      return "Sol couldn't find an earlier conversation about that.";
    case 'my_day':
      // **AN EMPTY DAY IS GOOD NEWS, AND IT IS SAID AS SUCH** (S12-03) — never
      // a Capture sentence about records he did not ask about, and never
      // confused with Crewlink being unable to work out his day, which is a
      // failure with its own words.
      return 'Nothing on your My Day matches that.';
    case 'web':
      // **THE SEARCH HAPPENED AND FOUND NOTHING USABLE** (I-078) — different
      // from "the search failed", and different again from a Crewlink record
      // that does not exist. Never a Capture sentence, and never an answer
      // improvised from general knowledge to fill the gap.
      return "Sol searched the web and didn't find a usable source for that.";
    default: {
      const exhaustive: never = domain;
      return exhaustive;
    }
  }
}

/** `'this Project'` / `'that Project'` / null for a read across every Project. */
function scopeNoun(scope: SolScopeFacts, currentProjectId: string | null): string | null {
  const searched = scope.scopesSearched[0] ?? scope.requestedScope;
  if (searched.kind === 'global') return null;
  return currentProjectId !== null && searched.projectId === currentProjectId
    ? 'this Project'
    : 'that Project';
}

/**
 * Whether the "Searched …" note belongs under an empty result of this domain.
 * Reports and photos are Project-scoped reads and say their scope in the
 * sentence itself; a Capture search keeps the note it always had; a Project
 * list and the company details are not scoped to a Project, so a "Searched this
 * Project" note under them would be false.
 */
export function noEvidenceScopeNoteApplies(domain: SolReadDomain): boolean {
  return domain === 'capture';
}

export const CLOSE_MATCHES_TEXT =
  'No exact match. These are the closest Captures Crewlink found — check them yourself.';

export const OVERSIZED_EVIDENCE_TEXT =
  'The closest Capture is too long for Sol to read safely, so Sol did not answer from part of it. Open the Capture and read it.';

/**
 * `reasoning_required` cannot reach a client — it is resolved inside the
 * endpoint. **A CONTRACT FAILURE, NOT A UX STATE**, and likewise for a claim
 * citing a source card the response did not carry.
 */
export const CONTRACT_FAILURE_TEXT =
  "Crewlink couldn't show that answer safely, so it was not shown.";

export const SIGNED_OUT_TEXT = 'Sign in to Crewlink and ask again.';

export const ASK_UNAVAILABLE_TEXT = "Crewlink couldn't reach Sol just now.";

// ===========================================================================
// Confirmed actions (M11 / S11-01)
// ===========================================================================

/**
 * **THE SENTENCE THAT MAKES THE CARD HONEST.** It says three things Brandon
 * needs before he taps: this changes SAVED information, nothing has happened
 * yet, and he is the one deciding. The design export's own wording is the
 * source — "This is a change to saved information, so confirm it first."
 */
export const ACTION_CONFIRM_PROMPT = 'This is a change to saved information, so confirm it first.';

/**
 * Success, and it names the change rather than saying "Done".
 *
 * **"performed through Sol" IS THE PROVENANCE, SAID OUT LOUD.** The Work Log
 * records the same thing, and the screen must not be quieter about it than the
 * history is.
 */
export function actionConfirmedText(captureLabel: string, statusLabel: string): string {
  return `${captureLabel} changed to ${statusLabel}. Work Log updated — performed through Sol.`;
}

/** Cancelled. **Nothing was sent**, so the sentence must not imply a rollback. */
export const ACTION_CANCELLED_TEXT = 'Nothing was changed.';

/**
 * The proposal timed out. **Asking again is the right advice here** — the
 * Capture is untouched and a fresh question produces a fresh proposal.
 */
export const ACTION_EXPIRED_TEXT =
  'That confirmation is no longer valid. Ask Sol again. Nothing was changed.';

/**
 * The Capture moved between the proposal and the tap. **"Try again" would be
 * the wrong advice**: the screen is out of date, so the honest instruction is
 * to look at the Capture.
 */
export const ACTION_STALE_TEXT =
  'This Capture changed somewhere else, so nothing was changed. Open it to see where it stands.';

export const ACTION_FAILED_TEXT = "Crewlink couldn't complete that change. Nothing was changed.";

export const ACTION_SIGNED_OUT_TEXT = 'Sign in to Crewlink and ask again. Nothing was changed.';

// ===========================================================================
// Long-term memory (M11 / D-040)
// ===========================================================================

/** Crewlink's words for the three memory kinds and two origins. Never the enum. */
export const MEMORY_KIND_LABEL: Readonly<Record<SolMemoryKind, string>> = {
  preference: 'Preference',
  instruction: 'Instruction',
  business_context: 'Business context',
};

export const MEMORY_ORIGIN_LABEL: Readonly<Record<SolMemoryOrigin, string>> = {
  explicit: 'You asked Sol to remember this',
  automatic: 'Sol noted this from something you said',
};

/** The acknowledgement under a remembered memory. The words are shown beside it. */
export function memorySavedText(replacedContent: string | null): string {
  return replacedContent === null ? 'Sol will remember that.' : 'Sol updated what it remembers.';
}

export const MEMORY_FORGOTTEN_TEXT = 'Sol has forgotten that.';

/** "Forget X" matched several, or none. **Never a guess** — a question, or an honest no. */
export function memoryClarificationText(
  candidateCount: number,
  description: string | null,
): string {
  const said = quoted(description);
  if (candidateCount === 0) {
    return said === null
      ? "Sol doesn't remember anything like that."
      : `Sol doesn't remember anything about ${said}.`;
  }
  return 'Sol remembers more than one thing like that. Which one should it forget?';
}

export function memoryClearPromptText(activeCount: number): string {
  if (activeCount === 0) return "Sol doesn't remember anything yet, so there is nothing to clear.";
  return activeCount === 1
    ? 'This clears the one thing Sol remembers. Confirm it first.'
    : `This clears all ${activeCount} things Sol remembers. Confirm it first.`;
}

export const MEMORY_CLEARED_TEXT = 'Sol has forgotten everything it remembered.';
export const MEMORY_CLEAR_CANCELLED_TEXT = 'Nothing was cleared.';
export const MEMORY_CLEAR_FAILED_TEXT = "Crewlink couldn't clear that. Nothing was cleared.";

/**
 * Why a memory request was declined. **Plain, and never an engineering code.**
 * A declined memory is not a failure of Sol — it is the policy working.
 */
export function memoryRefusedText(reason: SolMemoryRefusalReason): string {
  switch (reason) {
    case 'not_from_current_statement':
      return 'Sol only remembers things in your own words. Say it directly and ask Sol to remember it.';
    case 'sensitive':
      return "Sol doesn't keep passwords, card numbers or other sensitive details.";
    case 'target_not_sent':
      return "Sol isn't sure which memory you mean. Open Memory to pick it, or say it in full.";
    case 'too_long':
      return 'That is too long to remember. Keep it to a sentence or two.';
    case 'empty':
      return "Sol didn't catch what to remember. Say it again.";
    case 'business_context_not_automatic':
      return "Sol won't note that on its own. Ask it to remember it, and it will.";
    case 'unknown_kind':
    case 'unavailable':
      return "Sol couldn't save that memory just now. Try again.";
    default: {
      const exhaustive: never = reason;
      void exhaustive;
      return "Sol couldn't save that memory just now.";
    }
  }
}

/** The chip under an answer when Sol noted something automatically. Visible, always. */
export const MEMORY_NOTED_LABEL = 'Sol noted';
export const MEMORY_FORGET_ACTION = 'Forget';
export const MEMORY_FORGOTTEN_CHIP_TEXT = 'Forgotten.';

// ===========================================================================
// Conversation history (M11 / D-041)
// ===========================================================================

/** A conversation with no derived title yet. */
export const UNTITLED_CONVERSATION = 'Untitled conversation';

/**
 * **THE SENTENCE THAT MAKES DELETE HONEST.** Said before the tap, every time.
 *
 * **UPDATED ON JERRY'S APPROVED PRODUCT RULE, 2026-08-22 (migration `0024`).**
 * It used to say that what Sol noted automatically would go with the
 * conversation. That is no longer true and was the wrong model: once something
 * is visible in Sol memory it is Brandon's, whatever produced it, and only the
 * Memory screen or an explicit "forget" removes it. The copy now says the one
 * thing he needs before deciding — the chat goes, the memories stay — without
 * making it sound dangerous.
 */
export const CONVERSATION_DELETE_PROMPT =
  'This deletes this conversation. Saved Sol memories from it are kept, and you can manage them in Sol memory.';

export const CONVERSATION_DELETED_TEXT = 'Conversation deleted.';
export const CONVERSATION_ARCHIVED_TEXT = 'Archived. Memories are unchanged.';
export const CONVERSATION_UNARCHIVED_TEXT = 'Restored to your list.';

/** Where a conversation was started, in words. */
export function conversationOriginLabel(projectName: string | null, isGlobal: boolean): string {
  if (isGlobal) return 'Global Sol';
  return projectName === null ? 'A Project' : projectName;
}

/* ==========================================================================
 * WD-2 — Sol My Day proposal wording. Deterministic, and honest about what
 * Confirm will and will not do.
 * ========================================================================== */

/** The follow-up moved between the proposal and the tap. Look, don't retry. */
export const MY_DAY_ACTION_STALE_TEXT =
  'This follow-up changed somewhere else, so nothing was changed. Open My Day to see where it stands.';

/** Success, per verb — names the change, and says it went through Sol. */
export function myDayActionConfirmedText(action: string, title: string): string {
  switch (action) {
    case 'my_day_create':
      return `Follow-up added: “${title}”. Confirmed through Sol.`;
    case 'my_day_done':
      return `“${title}” marked done. Confirmed through Sol.`;
    case 'my_day_dismiss':
      return `“${title}” dismissed. Confirmed through Sol.`;
    case 'my_day_edit':
      return `“${title}” updated. Confirmed through Sol.`;
    case 'my_day_reschedule':
      return `“${title}” rescheduled. Confirmed through Sol.`;
    default:
      return `“${title}” updated. Confirmed through Sol.`;
  }
}

/** What the Confirm button is agreeing to, per verb — one plain headline. */
export function myDayProposalHeadline(display: {
  readonly action: string;
  readonly mode: string | null;
}): string {
  switch (display.action) {
    case 'create':
      return 'Add this follow-up?';
    case 'done':
      return 'Mark this follow-up done?';
    case 'dismiss':
      // **THE THREE DISMISSALS ARE THREE DIFFERENT SENTENCES**, because a
      // recurring follow-up's "get rid of it" has two honest meanings and the
      // card must state the one that was resolved before asking for a tap.
      return display.mode === 'skip_occurrence'
        ? 'SKIP THIS OCCURRENCE — the series continues?'
        : display.mode === 'end_series'
          ? 'END THE SERIES — no more occurrences?'
          : 'Dismiss this follow-up?';
    case 'edit':
      return 'Change this follow-up?';
    case 'reschedule':
      return 'Move this follow-up?';
    default:
      return 'Confirm this change?';
  }
}
